import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { UserAvatar } from "@/components/Navbar";
import MiniFlip from "@/components/MiniFlip";
import StreakCard from "@/components/StreakCard";
import BadgeGrid from "@/components/BadgeGrid";
import { ArrowLeft, Trophy, TrendingUp, MessageCircle, Target, CheckCircle, XCircle, Heart } from "lucide-react";

export default function UserProfile() {
  const { userId } = useParams();
  const { api } = useAuth();
  const [profile, setProfile] = useState(null);
  const [voteHistory, setVoteHistory] = useState([]);
  const [userComments, setUserComments] = useState([]);
  const [tab, setTab] = useState("votes");
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await api.get(`/users/${userId}/profile`);
      setProfile(data);
    } catch (err) {
      console.error("Failed to load profile:", err);
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  const fetchVotes = useCallback(async () => {
    try {
      const { data } = await api.get(`/users/${userId}/votes`);
      setVoteHistory(data);
    } catch {}
  }, [api, userId]);

  const fetchComments = useCallback(async () => {
    try {
      const { data } = await api.get(`/users/${userId}/comments`);
      setUserComments(data);
    } catch {}
  }, [api, userId]);

  useEffect(() => { fetchProfile(); fetchVotes(); fetchComments(); }, [fetchProfile, fetchVotes, fetchComments]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-100 rounded w-20" />
          <div className="flex gap-4 items-center"><div className="w-16 h-16 bg-gray-100 rounded-full" /><div className="h-6 bg-gray-100 rounded w-32" /></div>
          <div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl" />)}</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center">
        <p className="text-neutral">User not found.</p>
        <Link to="/" className="text-view-a text-sm font-semibold mt-2 inline-block">Go home</Link>
      </div>
    );
  }

  const stats = [
    { label: "Votes", value: profile.total_votes, icon: TrendingUp, color: "text-view-a" },
    { label: "Wins", value: profile.wins, icon: Trophy, color: "text-success" },
    { label: "Win Rate", value: `${profile.win_rate}%`, icon: Target, color: "text-purple-500" },
    { label: "Comments", value: profile.comment_count, icon: MessageCircle, color: "text-amber-500" },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-6" data-testid="user-profile">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-neutral hover:text-dark transition-colors mb-4" data-testid="profile-back-link">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      {/* Profile header */}
      <div className="bg-white rounded-2xl shadow-card p-6 mb-4">
        <div className="flex items-center gap-4 mb-5">
          <UserAvatar user={profile} size="w-14 h-14" textSize="text-lg" />
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl text-dark tracking-tight" data-testid="profile-name">{profile.name}</h1>
            <p className="text-sm text-neutral">{profile.email}</p>
            {profile.role === "admin" && (
              <span className="inline-block mt-1 text-[10px] font-bold text-view-a bg-view-a-light px-2 py-0.5 rounded-full uppercase tracking-wider">Admin</span>
            )}
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="profile-stats">
          {stats.map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center">
              <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} />
              <div className="font-heading text-xl text-dark">{s.value}</div>
              <div className="text-[11px] text-neutral font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <StreakCard profile={profile} />

      <BadgeGrid badges={profile.badges} badgeCount={profile.badge_count} profileName={profile.name} />

      {/* Tabs */}
      <div className="flex gap-1 mb-4" data-testid="profile-tabs">
        {["votes", "comments"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2 text-sm font-semibold rounded-xl transition-colors ${
              tab === t ? "bg-dark text-white" : "bg-white text-neutral hover:text-dark shadow-card"
            }`}
            data-testid={`tab-${t}`}
          >
            {t === "votes" ? `Votes (${voteHistory.length})` : `Comments (${userComments.length})`}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "votes" ? (
        <div className="space-y-3" data-testid="vote-history">
          {voteHistory.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-card p-8 text-center"><p className="text-neutral text-sm">No votes yet.</p></div>
          ) : (
            voteHistory.map((v) => (
              <Link
                key={v.vote_id || v.topic_id}
                to={`/issue/${v.topic_id}`}
                className="block bg-white rounded-xl shadow-card p-4 hover:shadow-card-hover transition-all"
                data-testid={`vote-item-${v.topic_id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-semibold text-dark leading-snug flex-1 mr-3">{v.topic?.question}</h3>
                  {v.is_winning ? (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-success bg-green-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                      <CheckCircle className="w-3 h-3" /> Winning
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-view-b bg-view-b-light px-2 py-0.5 rounded-full whitespace-nowrap">
                      <XCircle className="w-3 h-3" /> Losing
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${v.side === "a" ? "text-view-a bg-view-a-light" : "text-view-b bg-view-b-light"}`}>
                    Voted {v.side === "a" ? v.topic?.side_a_label : v.topic?.side_b_label}
                  </span>
                </div>
                <MiniFlip votesA={v.votes_a} votesB={v.votes_b} />
              </Link>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="comment-history">
          {userComments.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-card p-8 text-center"><p className="text-neutral text-sm">No comments yet.</p></div>
          ) : (
            userComments.map((c) => (
              <Link
                key={c.comment_id}
                to={`/issue/${c.topic_id}`}
                className="block bg-white rounded-xl shadow-card p-4 hover:shadow-card-hover transition-all"
                data-testid={`comment-item-${c.comment_id}`}
              >
                <p className="text-xs text-neutral mb-1 font-medium">{c.topic?.question}</p>
                <p className="text-sm text-dark">{c.text}</p>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-neutral">
                  <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {c.likes || 0}</span>
                </div>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}


